import { FEATURE_FLAGS, TRACKING } from '@/config'
import { APPLICATION_TYPE_REDIRECT } from '@/jobapplication/constants'
import * as tracking from '@/tracking'
import { assertInMap } from '@/utils/assert'
import { createUrlParser, appendQueryParams, getUrlOrigin } from '@/utils/url'

import {
  JOB_STATUS_INVISIBLE,
  JOB_TYPE_BACKFILL
} from '../../jobDetail/constants'
import { COMPANY, JOB } from '../../search/constants'
import {
  applicationTypeToGtmUserInteractionCategory,
  GTM_EVENT_INITIAL_SEARCH,
  GTM_EVENT_MANUAL_SEARCH,
  GTM_EVENT_PAGE_VIEW,
  GTM_EVENT_USER_INTERACTION,
  GTM_EVENT_USER_INTERACTION_ACTION_CLICK,
  GTM_EVENT_USER_INTERACTION_ACTION_NEW_ATTACHMENTS,
  GTM_EVENT_USER_INTERACTION_ACTION_NO_ATTACHMENTS,
  GTM_EVENT_USER_INTERACTION_ACTION_PREVIOUS_ATTACHMENTS,
  GTM_EVENT_USER_INTERACTION_ACTION_REUSE_ATTACHMENTS_TICKED,
  GTM_EVENT_USER_INTERACTION_ACTION_SCROLL,
  GTM_EVENT_USER_INTERACTION_ACTION_SUBMIT_BUTTON_CLICKED,
  GTM_EVENT_USER_INTERACTION_ACTION_VIEWS,
  GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION,
  GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION_BACKFILL,
  GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION_BACKFILL_LAYER,
  GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION_EXPRESS,
  GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION_EXTERNAL,
  GTM_EVENT_USER_INTERACTION_CATEGORY_AUTO_LOAD_NEXT_PAGE,
  GTM_EVENT_USER_INTERACTION_CATEGORY_SHOW_APPLY_FILE_ATTACHMENTS,
  GTM_EVENT_USER_INTERACTION_CATEGORY_REUSE_APPLY_FILE_ATTACHMENTS,
  GTM_EVENT_USER_INTERACTION_CATEGORY_MICROCONVERSION,
  GTM_EVENT_USER_INTERACTION_CATEGORY_SCROLL_JOB,
  GTM_EVENT_USER_INTERACTION_CATEGORY_JOBMAIL,
  GTM_EVENT_USER_INTERACTION_CATEGORY_PAGINATION_BUTTON,
  GTM_EVENT_USER_INTERACTION_CATEGORY_RATING,
  GTM_EVENT_USER_INTERACTION_CATEGORY_FORMS,
  GTM_EVENT_USER_INTERACTION_LABEL_APPLICATION,
  GTM_EVENT_USER_INTERACTION_LABEL_RECRUITING,
  GTM_EVENT_USER_INTERACTION_LABEL_NEWSLETTER_SLIDE,
  GTM_EVENT_USER_INTERACTION_LABEL_JOB_SEARCH,
  INTERNAL_COMPANY_EVENT_WEBSITE,
  INTERNAL_JOB_EVENT_APP_ACTION_EXTERN,
  MICROCONVERSION_EVENT_CLICK_COMPANY_WEBSITE,
  searchTypeToMicroconversionCopyToCliboard,
  searchTypeToMicroconversionNavigateNext,
  searchTypeToInternalViewEvent,
  GTM_EVENT_USER_INTERACTION_CATEGORY_JOBVIEW,
  GTM_EVENT_USER_INTERACTION_ACTION_JOBVIEW,
  GTM_EVENT_USER_INTERACTION_ACTION_QUALIFIED_JOBVIEW,
  GTM_EVENT_USER_INTERACTION_CATEGORY_APPLY_FORM_UNSOLICITED,
  GTM_EVENT_USER_INTERACTION_CATEGORY_APPLY_REDIRECT_UNSOLICITED,
  GTM_EVENT_USER_INTERACTION_LABEL_APPLICATION_UNSOLICITED,
  INTERNAL_JOB_EVENT_APP_VIEW,
  INTERNAL_COMPANY_EVENT_APP_VIEW_INTERN,
  INTERNAL_COMPANY_EVENT_APP_VIEW_EXTERN,
  INTERNAL_COMPANY_EVENT_APP_ACTION_EXTERN,
  GTM_EVENT_RATING_WIDGET,
  GTM_EVENT_JOB_FOR_APPLICATION,
  GTM_EVENT_USE_GEOLOCATION
} from '../constants'

import { trackInGtm, gtmPush } from './_base'
import trackInternalEvent from './trackInternalEvent'

// store history reference for use in getCurrentPageUrl
let history

/**
 * Extract page url (pathname + search) from current location
 * @return {string}
 */
const getCurrentPageUrl = () => {
  const location = history.createHref(history.location)
  // url parser will resolve relative urls
  const url = createUrlParser(location)
  // generate page path
  return url.pathname + url.search
}

/**
 * Track the current page view
 * @param {Object} opts - additional parameters
 */
export const trackCurrentPageView = (
  opts = {}
) => {
  const page = getCurrentPageUrl()

  trackInGtm(GTM_EVENT_PAGE_VIEW, {
    // allow override of page param
    page,
    ...opts
  })
}

/**
 * Track an user interaction event
 * @param {Object} opts
 * @param {String} interactionLabel
 * @param {String} interactionCategory
 * @param {String} interactionAction
 * @param {Number} interactionValue
 */
export const trackUserInteraction = (
  {
    label: interactionLabel,
    category: interactionCategory,
    action: interactionAction,
    value: interactionValue,
    ...opts
  }
) => {
  trackInGtm(GTM_EVENT_USER_INTERACTION, {
    page: getCurrentPageUrl(),
    interactionLabel,
    interactionCategory,
    interactionAction,
    interactionValue,
    // allow override of page
    ...opts
  })
}

/**
 * Track micro conversions
 * @param {String} label - microconversion label
 * @param {Object} opts - optional tracking parameters
 */
export function trackMicroConversion (label, opts = {}) {
  trackUserInteraction({
    ...opts,
    label: label,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_MICROCONVERSION,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK
  })
}

export function trackJobScroll (opts = {}) {
  trackUserInteraction({
    ...opts,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_SCROLL_JOB,
    action: GTM_EVENT_USER_INTERACTION_ACTION_SCROLL
  })
}

/**
 * Track initial search results
 * @param {Number[]} searchResultsList - list of result ids
 * @param {Object} opts - additional parameters
 */
export const trackInitialSearch = (searchResultsList, opts = {}) => {
  trackInGtm(GTM_EVENT_INITIAL_SEARCH, { searchResultsList, ...opts })
}

/**
 * Track manual search
 * @param {String} location - url of the search to track
 * @param {Object} opts - additional parameters
 */
export const trackManualSearch = (location, opts = {}) => {
  trackInGtm(GTM_EVENT_MANUAL_SEARCH, { location, ...opts })
}

/**
 * Track jobboersencheck rating widget
 * @param {Object} opts - additional parameters
 */
export const trackRatingWidget = (opts = {}) => {
  trackInGtm(GTM_EVENT_RATING_WIDGET, { ...opts })
}

/**
 * Track search initiated from a CMS page
 */
export const trackSearchInitiatedFromCMSPage = () => {
  trackUserInteraction({
    label: GTM_EVENT_USER_INTERACTION_LABEL_JOB_SEARCH,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_FORMS,
    action: GTM_EVENT_USER_INTERACTION_ACTION_SUBMIT_BUTTON_CLICKED
  })
}

export function trackExpressApplicationSend (opts = {}) {
  trackUserInteraction({
    ...opts,
    label: GTM_EVENT_USER_INTERACTION_LABEL_APPLICATION,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION_EXPRESS,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK
  })
}

export function trackApplicationSend (opts = {}) {
  trackUserInteraction({
    ...opts,
    label: GTM_EVENT_USER_INTERACTION_LABEL_APPLICATION,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK
  })
}

export const trackJobApplicationExternal = (jobInfo, trackingInfo = {}) => {
  const { id: jobId, type, status: jobStatus, url: jobUrl } = jobInfo
  if (type !== JOB_TYPE_BACKFILL && jobStatus !== JOB_STATUS_INVISIBLE) {
    trackInternalEvent(INTERNAL_JOB_EVENT_APP_ACTION_EXTERN, jobId, JOB, { jobUrl })
  }
  trackUserInteraction({
    ...prepareJobInfo(jobInfo, trackingInfo),
    label: GTM_EVENT_USER_INTERACTION_LABEL_APPLICATION,
    category: type === JOB_TYPE_BACKFILL
      ? GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION_BACKFILL
      : GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION_EXTERNAL,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK
  })
}

export const trackJobApplicationExternalModal = (jobInfo, trackingInfo = {}) => {
  trackUserInteraction({
    ...prepareJobInfo(jobInfo, trackingInfo),
    label: GTM_EVENT_USER_INTERACTION_LABEL_APPLICATION,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION_BACKFILL_LAYER,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK
  })
}

export const trackJobApplicationClick = (jobInfo, trackingInfo = {}) => {
  const { id: jobId, application: { url, type: applicationType }, status: jobStatus, url: jobUrl } = jobInfo
  if (!assertInMap(
    applicationType,
    applicationTypeToGtmUserInteractionCategory,
    `Apply button tracking for '${applicationType}' application type is not allowed`,
    (scope) => scope.setTag('jobId', jobId)
  )) {
    return
  }

  if (url) {
    trackUserInteraction({
      ...prepareJobInfo(jobInfo, trackingInfo),
      label: `${jobId}`,
      category: applicationTypeToGtmUserInteractionCategory[applicationType],
      action: url
    })
  }
  if (jobStatus !== JOB_STATUS_INVISIBLE) {
    trackInternalEvent(INTERNAL_JOB_EVENT_APP_VIEW, jobId, JOB, { jobUrl })
  }
}

export const trackCompanyApplicationClick = (companyInfo) => {
  const { id: companyId, application: { type, url } } = companyInfo

  const isExtern = type === APPLICATION_TYPE_REDIRECT

  if (url) {
    const category = isExtern
      ? GTM_EVENT_USER_INTERACTION_CATEGORY_APPLY_REDIRECT_UNSOLICITED
      : GTM_EVENT_USER_INTERACTION_CATEGORY_APPLY_FORM_UNSOLICITED

    trackUserInteraction({
      label: `${companyId}`,
      category,
      action: url
    })

    const event = isExtern
      ? INTERNAL_COMPANY_EVENT_APP_VIEW_EXTERN
      : INTERNAL_COMPANY_EVENT_APP_VIEW_INTERN
    trackInternalEvent(event, companyId, COMPANY)

    if (isExtern) {
      trackInternalEvent(INTERNAL_COMPANY_EVENT_APP_ACTION_EXTERN, companyId, COMPANY)
    }
  }
}

export const trackCompanyJobApplicationSend = ({ companyId, ...opts }) => {
  trackUserInteraction({
    ...opts,
    label: GTM_EVENT_USER_INTERACTION_LABEL_APPLICATION_UNSOLICITED,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_APPLICATION,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK
  })
}

export function prepareJobInfo ({
  id,
  tracking,
  url,
  sourceJobId,
  application: { url: applicationUrl } = {},
  kununu
}, {
  jobOpening,
  trackKununuListRating = true,
  isNimm2Feature = false,
  loop = null
} = {}) {
  const trackingInfo = {
    ...TRACKING,
    isJobHit: true,
    jobId: id,
    applicationUrl,
    listKununuRating: trackKununuListRating && !!kununu?.score,
    jobOpening,
    isNimm2Feature,
    loop,
    ...tracking
  }

  if (!sourceJobId || id === sourceJobId) {
    return trackingInfo
  }

  // similar jobs require an override of the reported page url
  let page = appendQueryParams(url, { 'source-job-id': sourceJobId })
  page = page.substr(getUrlOrigin(page).length)
  return {
    ...trackingInfo,
    page
  }
}

export function prepareCompanyInfo ({
  id,
  tracking,
  kununu
}, {
  profileOpening = false,
  trackKununuListRating = true
} = {}) {
  return {
    ...TRACKING,
    companyId: id,
    listKununuRating: trackKununuListRating && !!kununu?.score,
    profileOpening,
    isNimm2Feature: false,
    loop: null,
    ...tracking
  }
}

export const trackOnContentLoaded = ({ id, info, type, jobUrl } = {}) => {
  trackCurrentPageView(info)

  if (!type) {
    return
  }
  if (!assertInMap(
    type,
    searchTypeToInternalViewEvent,
    `Track content loaded for '${type}' search type is not allowed`,
    (scope) => scope.setTag('id', id)
  )) {
    return
  }

  if (id) {
    if (type === 'job') {
      trackJobview(id, info)
    }
    // track internally all non-backfill jobs
    if (!info.isBackfillJob) {
      if (type === 'job') {
        trackInternalEvent(searchTypeToInternalViewEvent[type], id, type, { jobUrl })
      } else {
        trackInternalEvent(searchTypeToInternalViewEvent[type], id, type)
      }
    }
  }
}

export const initLocation = (_history, sourceJobId) => {
  history = _history
  // current location without any hash, note the use of global.location instead of history
  // that is to prevent any possible initial url change affecting the originalLocation
  const url = global.location.protocol + '//' + global.location.hostname +
    global.location.pathname + global.location.search

  const originalLocation = sourceJobId ? appendQueryParams(url, { 'source-job-id': sourceJobId }) : url

  // push to gtm, so it's available for all future events
  gtmPush({ originalLocation })

  // report feature flags in analytics
  if (FEATURE_FLAGS) {
    const featureFlags = {
      event: 'feature'
    }
    Object.keys(FEATURE_FLAGS).forEach(name => {
      featureFlags['feature_' + name] = FEATURE_FLAGS[name].value ? 'on' : 'off'
    })
    gtmPush(featureFlags)
  }
}

export const trackRecruitingInteraction = (category) => {
  trackUserInteraction({
    category: category,
    label: GTM_EVENT_USER_INTERACTION_LABEL_RECRUITING,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK
  })
}

export const trackCopyToClipboard = (
  {
    type = 'job',
    trackingInfo,
    ...scopeParams
  } = {}
) => {
  if (!assertInMap(
    type,
    searchTypeToMicroconversionCopyToCliboard,
    `Track copy to clipboard for '${type}' search type is not allowed`,
    scopeParams
  )) {
    return
  }

  tracking.trackMicroConversion(searchTypeToMicroconversionCopyToCliboard[type], trackingInfo)
}

export const trackNavigateNext = (
  {
    type = 'job'
  } = {}
) => {
  if (!assertInMap(
    type,
    searchTypeToMicroconversionNavigateNext,
    `Track navigate next for '${type}' search type is not allowed`
  )) {
    return
  }

  trackMicroConversion(searchTypeToMicroconversionNavigateNext[type])
}

export const trackJobmailActivation = (label) => {
  trackUserInteraction({
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_JOBMAIL,
    action: getCurrentPageUrl(),
    label
  })
}

export const trackCompanyProfileWebsiteClick = (trackingInfo) => {
  trackMicroConversion(MICROCONVERSION_EVENT_CLICK_COMPANY_WEBSITE, trackingInfo)
  trackInternalEvent(INTERNAL_COMPANY_EVENT_WEBSITE, trackingInfo.companyId, COMPANY)
}

export const trackNewsletterPopupOpens = (category) => {
  trackUserInteraction({
    category,
    action: getCurrentPageUrl(),
    label: GTM_EVENT_USER_INTERACTION_LABEL_NEWSLETTER_SLIDE
  })
}

export const trackReuseAttachmentsView = (areAttachmentsVisible) => {
  // Track if reusing attachments is available
  trackUserInteraction({
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_SHOW_APPLY_FILE_ATTACHMENTS,
    action: areAttachmentsVisible ? GTM_EVENT_USER_INTERACTION_ACTION_PREVIOUS_ATTACHMENTS : GTM_EVENT_USER_INTERACTION_ACTION_NO_ATTACHMENTS,
    label: getCurrentPageUrl()
  })
}

export const trackReuseAttachmentsUsage = (areAttachmentsReused) => {
  // Track if users use reuse attachments option
  trackUserInteraction({
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_REUSE_APPLY_FILE_ATTACHMENTS,
    action: areAttachmentsReused ? GTM_EVENT_USER_INTERACTION_ACTION_PREVIOUS_ATTACHMENTS : GTM_EVENT_USER_INTERACTION_ACTION_NEW_ATTACHMENTS,
    label: getCurrentPageUrl()
  })
}

export const trackReusedAttachmentsTicked = () => {
  trackUserInteraction({
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_REUSE_APPLY_FILE_ATTACHMENTS,
    action: GTM_EVENT_USER_INTERACTION_ACTION_REUSE_ATTACHMENTS_TICKED,
    label: getCurrentPageUrl()
  })
}

export const trackPaginationButtonClicks = () => {
  trackUserInteraction({
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_PAGINATION_BUTTON,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK
  })
}

export const trackAutoLoadNextPage = () => {
  trackUserInteraction({
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_AUTO_LOAD_NEXT_PAGE,
    action: GTM_EVENT_USER_INTERACTION_ACTION_SCROLL
  })
}

export const trackRatingViews = (ratingChannel) => {
  trackUserInteraction({
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_RATING,
    action: GTM_EVENT_USER_INTERACTION_ACTION_VIEWS,
    label: ratingChannel
  })
}

export const trackRatingClicks = (ratingChannel) => {
  trackUserInteraction({
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_RATING,
    action: GTM_EVENT_USER_INTERACTION_ACTION_CLICK,
    label: ratingChannel
  })
}

export const trackJobview = (jobId, trackInfo, params = {}) => {
  trackUserInteraction({
    ...trackInfo,
    ...params,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_JOBVIEW,
    action: GTM_EVENT_USER_INTERACTION_ACTION_JOBVIEW,
    label: `${jobId}`
  })
}

export const trackQualifiedJobViewInteraction = (jobId, trackInfo, opts = {}) => {
  trackUserInteraction({
    ...opts,
    ...trackInfo,
    category: GTM_EVENT_USER_INTERACTION_CATEGORY_JOBVIEW,
    action: GTM_EVENT_USER_INTERACTION_ACTION_QUALIFIED_JOBVIEW,
    label: `${jobId}`
  })
}

export const trackQualifiedJobView = (gtmEvent) => {
  trackInGtm(gtmEvent, { page: getCurrentPageUrl() })
}

export const trackJobForApplicationRequest = (loop) => {
  trackInGtm(GTM_EVENT_JOB_FOR_APPLICATION, { loop })
}

export const trackJobForApplicationSuccess = (loop, success) => {
  trackInGtm(GTM_EVENT_JOB_FOR_APPLICATION, { loop, success })
}

export const trackCurrentLocationRequest = () => {
  trackInGtm(GTM_EVENT_USE_GEOLOCATION, {
    allow: false,
    reject: false,
    success: false
  })
}

export const trackCurrentLocationAllow = () => {
  trackInGtm(GTM_EVENT_USE_GEOLOCATION, {
    allow: true,
    success: false,
    reject: false
  })
}

export const trackCurrentLocationReject = () => {
  trackInGtm(GTM_EVENT_USE_GEOLOCATION, {
    allow: false,
    reject: true,
    success: false
  })
}

export const trackCurrentLocationSuccess = () => {
  trackInGtm(GTM_EVENT_USE_GEOLOCATION, {
    allow: true,
    success: true,
    reject: false
  })
}
